import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { CurrencyCodes } from '@onevesthq/ov-enums';
import type {
  PortfolioSecurity,
  PortfolioSubAccount,
  PortfolioSubAccountHoldingReport,
} from '@ovComponents/4-module/widgets/portfolio/components/interfaces';
import {
  buildPortfolioSubAccount,
  buildPortfolioSubAccountHolding,
  buildSecurity,
  groupSecuritiesByAssetClass,
  isSecurityHeldInSubAccount,
  type PortfolioSecurityHoldingInput,
  type PortfolioSubAccountReportInput,
  sortPortfolioReportSubAccounts,
  sortSecurities,
} from '@ovComponents/4-module/widgets/portfolio/components/utils';

export type PortfolioAllocationView = {
  subAccounts: PortfolioSubAccount[];
  securities: PortfolioSecurity[];
};

export type AssetClassGroupView = NonNullable<
  ReturnType<typeof groupSecuritiesByAssetClass>
>[number] & {
  startIndex: number;
};

export type PortfolioAllocationViewReport = {
  subAccounts: PortfolioSubAccountReportInput[];
  holdings: PortfolioSecurityHoldingInput[];
};

// Absent amounts are unknown, not zero. Defaulting them before this check
// would drop every row of a report that selects no money fields.
const isSkippedZeroPortfolioHolding = (holding: {
  taxRank?: number | null;
  expectedValueCents?: number;
  valueCents?: number;
}) =>
  !holding.taxRank &&
  holding.expectedValueCents === 0 &&
  holding.valueCents === 0;

const hasSubAccountHoldingBalance = (holding: {
  valueCents?: number;
  quantity?: number;
}) => (holding.valueCents ?? 0) !== 0 || (holding.quantity ?? 0) !== 0;

const buildSubAccountOnlyPortfolioHolding = (
  subAccountHolding: PortfolioSubAccountHoldingReport,
): PortfolioSecurityHoldingInput => ({
  ...subAccountHolding,
  expectedValueCents: 0,
  expectedPercentage: 0,
  valueCents: 0,
  quantity: 0,
  pendingQuantity: 0,
});

const sleeveOnlyProductId = (
  holding: PortfolioSubAccountHoldingReport,
  portfolioHoldingProductIds: Set<string>,
) => {
  const productId = holding.financialProduct?.id;

  if (!productId || portfolioHoldingProductIds.has(productId)) {
    return undefined;
  }

  if (holding.financialProduct?.isCash === true) {
    return undefined;
  }

  if (!hasSubAccountHoldingBalance(holding)) {
    return undefined;
  }

  return productId;
};

const collectSubAccountOnlyHoldings = (
  portfolioReport: Pick<PortfolioAllocationViewReport, 'subAccounts'>,
  portfolioHoldingProductIds: Set<string>,
) => {
  const holdingsByProductId = new Map<string, PortfolioSecurityHoldingInput>();

  for (const subAccountReport of portfolioReport.subAccounts ?? []) {
    for (const holding of subAccountReport.holdings ?? []) {
      const productId = sleeveOnlyProductId(
        holding,
        portfolioHoldingProductIds,
      );

      if (!productId || holdingsByProductId.has(productId)) {
        continue;
      }

      holdingsByProductId.set(
        productId,
        buildSubAccountOnlyPortfolioHolding(holding),
      );
    }
  }

  return [...holdingsByProductId.values()];
};

const attachHoldingsToSubAccounts = (
  financialProductId: string,
  localPortfolioSubAccounts: PortfolioSubAccount[],
) =>
  localPortfolioSubAccounts.map((subAccount) => {
    const holding = subAccount?.holdings?.find(
      (h) => h.financialProduct.id === financialProductId,
    );

    return {
      ...subAccount,
      holding: holding && buildPortfolioSubAccountHolding(holding),
    };
  });

const sumAttachedSubAccountHoldingValueCents = (
  subAccounts: PortfolioSubAccount[],
) =>
  subAccounts.reduce(
    (sum, subAccount) => sum + (subAccount.holding?.valueCents ?? 0),
    0,
  );

// Omit valueCents: tradeable rollup value implies a visible sleeve hold; sleeve-only rows reuse valueCents for display only.
const hasNonZeroPortfolioLevelHolding = (security: PortfolioSecurity) =>
  (security.expectedPercentage ?? 0) !== 0 ||
  (security.expectedValueCents ?? 0) !== 0;

const withVisibleSubAccountOnlyValue = (
  security: PortfolioSecurity,
  isSubAccountVisible: (subAccount: PortfolioSubAccount) => boolean,
) =>
  security.isSubAccountOnly
    ? {
        ...security,
        valueCents: sumAttachedSubAccountHoldingValueCents(
          security.subAccounts.filter(isSubAccountVisible),
        ),
      }
    : security;

export const filterSecuritiesToVisibleHoldings = (
  securities: PortfolioSecurity[],
  isSubAccountVisible: (subAccount: PortfolioSubAccount) => boolean,
) =>
  securities
    .filter(
      (security) =>
        hasNonZeroPortfolioLevelHolding(security) ||
        security.subAccounts.some(
          (subAccount) =>
            isSubAccountVisible(subAccount) &&
            isSecurityHeldInSubAccount(subAccount, security.id),
        ),
    )
    .map((security) =>
      withVisibleSubAccountOnlyValue(security, isSubAccountVisible),
    );

export const buildPortfolioAllocationView = (args: {
  portfolioReport: PortfolioAllocationViewReport;
  t: Parameters<typeof buildPortfolioSubAccount>[1];
  translateBackendString: Parameters<typeof buildSecurity>[0];
}) => {
  const { portfolioReport, t, translateBackendString } = args;

  const localPortfolioSubAccounts: PortfolioSubAccount[] = [];

  for (const subAccountReport of portfolioReport?.subAccounts ?? []) {
    const { subAccount } = subAccountReport;

    if (!subAccount?.id) continue;

    localPortfolioSubAccounts.push(
      buildPortfolioSubAccount(subAccountReport, t),
    );
  }

  localPortfolioSubAccounts.sort(sortPortfolioReportSubAccounts);

  const localSecurities: PortfolioSecurity[] = [];
  const portfolioHoldingProductIds = new Set<string>();

  for (const security of portfolioReport?.holdings ?? []) {
    const { financialProduct } = security;

    if (!financialProduct?.id) continue;

    if (isSkippedZeroPortfolioHolding(security)) {
      continue;
    }

    portfolioHoldingProductIds.add(financialProduct.id);

    localSecurities.push(
      buildSecurity(
        translateBackendString,
        security,
        attachHoldingsToSubAccounts(
          financialProduct.id,
          localPortfolioSubAccounts,
        ),
      ),
    );
  }

  for (const security of collectSubAccountOnlyHoldings(
    portfolioReport,
    portfolioHoldingProductIds,
  )) {
    const { financialProduct } = security;

    if (!financialProduct?.id) continue;

    const subAccountsWithHoldings = attachHoldingsToSubAccounts(
      financialProduct.id,
      localPortfolioSubAccounts,
    );

    localSecurities.push({
      ...buildSecurity(
        translateBackendString,
        {
          ...security,
          valueCents: sumAttachedSubAccountHoldingValueCents(
            subAccountsWithHoldings,
          ),
        },
        subAccountsWithHoldings,
      ),
      isSubAccountOnly: true,
    });
  }

  localSecurities.sort(sortSecurities);

  return {
    subAccounts: localPortfolioSubAccounts,
    securities: localSecurities,
  };
};

export const buildCashSecurity = ({
  portfolioReport,
  ticker,
  translatedName,
  currency,
}: {
  portfolioReport:
    | Partial<
        Pick<PortfolioReport, 'expectedCashCents' | 'expectedCashPercentage'>
      >
    | null
    | undefined;
  ticker: string;
  translatedName: string;
  currency: CurrencyCodes;
}): PortfolioSecurity => ({
  id: 'CASH',
  ticker,
  expectedValueCents: portfolioReport?.expectedCashCents ?? 0,
  expectedPercentage: portfolioReport?.expectedCashPercentage ?? 0,
  currentPriceCents: 100,
  translatedName,
  taxRank: null,
  financialProductTaxRank: 99,
  valueCents: 0,
  subAccounts: [],
  currency,
});

export const buildAssetClassGroupView = (args: {
  securities: PortfolioSecurity[];
  isAssetClassPortfolio: boolean;
  t: (key: string) => string;
  translateBackendString: Parameters<typeof groupSecuritiesByAssetClass>[1];
}) => {
  const { securities, isAssetClassPortfolio, t, translateBackendString } = args;

  if (!isAssetClassPortfolio) {
    return null;
  }

  const groups = groupSecuritiesByAssetClass(
    securities,
    translateBackendString,
    () => t('modelPortfolios:unassigned'),
  );

  let startIndex = 0;
  return groups.map((group) => {
    const view = { ...group, startIndex };
    startIndex += group.items.length;

    return view;
  });
};
